import * as React from 'react';

import moment from 'moment';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import Download from '@mui/icons-material/Download';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
  ColorType,
  CrosshairMode,
} from 'lightweight-charts';

import { useThemeMode } from '../../../../../providers/theme-provider';
import { ReportFilterButtons } from '../../../../../components/report-filter-buttons';
import watermarkDark from '../../../../../assets/watermark.svg';
import watermarkLight from '../../../../../assets/watermark-light.svg';
import { IPortfolioPerformanceHistorical } from '../../../../../providers/portfolio-performance/portfolio-performance.types';

// ---------------------------------------------------------------------------
// Data shape
// ---------------------------------------------------------------------------

export type Month =
  'JAN' | 'FEB' | 'MAR' | 'APR' | 'MAY' | 'JUN' | 'JUL' | 'AUG' | 'SEP' | 'OCT' | 'NOV' | 'DEC';

type PerformancePayload = IPortfolioPerformanceHistorical;

// ---------------------------------------------------------------------------
// Scope / Granularity
// ---------------------------------------------------------------------------

type Scope = 'ITD' | 'YTD' | 'MTD';
type Granularity = 'D' | 'W' | 'M';

const SCOPE_OPTIONS = [
  { value: 'ITD', label: 'ITD', isVisible: true },
  { value: 'YTD', label: 'YTD', isVisible: true },
  { value: 'MTD', label: 'MTD', isVisible: true },
];

const GRANULARITY_OPTIONS = [
  { value: 'D', label: 'Daily', isVisible: true },
  { value: 'W', label: 'Weekly', isVisible: true },
  { value: 'M', label: 'Monthly', isVisible: true },
];

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export const MONTH_ORDER: Month[] = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

const MONTH_FULL: Record<Month, string> = {
  JAN: 'January',
  FEB: 'February',
  MAR: 'March',
  APR: 'April',
  MAY: 'May',
  JUN: 'June',
  JUL: 'July',
  AUG: 'August',
  SEP: 'September',
  OCT: 'October',
  NOV: 'November',
  DEC: 'December',
};

const MONTH_INDEX: Record<Month, number> = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
};

// ---------------------------------------------------------------------------
// Formatting / colour helpers
// ---------------------------------------------------------------------------

function fmtPct(r: number): string {
  const pct = r * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

function fmtPctDay(r: number): string {
  const pct = r * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
}

// The returns/metrics here are a periodic backend calc (not the live-ticking
// Market Risk feed) - showing when it last ran tells the user how fresh the
// numbers are, matching the "DD MMM YYYY - HH:mm:ss UTC" convention used for
// the transactions table's "Live Data" timestamp elsewhere on this page.
function fmtCalculatedAt(iso: string): string {
  return `${moment.parseZone(iso).utc().format('DD MMM YYYY - HH:mm:ss')} UTC`;
}

function returnBg(r: number, boost = 0): string {
  const a = Math.min(Math.abs(r) / 0.06, 1) * 0.14 + boost;
  return r >= 0 ? `rgba(54, 200, 138, ${a})` : `rgba(216, 44, 13, ${a})`;
}

function returnText(r: number, isDark: boolean): string {
  if (r >= 0) return isDark ? '#4ade80' : '#166534';
  return isDark ? '#f87171' : '#991b1b';
}

// ---------------------------------------------------------------------------
// Watermark — plain inline styling, not the nested `.chart-container
// .chart-box .watermark` SCSS selector from the chart-type component (that's
// a compound descendant selector, not a reusable global class, so it never
// matched here).
// ---------------------------------------------------------------------------

function Watermark({ isDark }: { isDark: boolean }): React.ReactElement {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundImage: `url(${isDark ? watermarkDark : watermarkLight})`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Data utility — bar / distribution computation
// ---------------------------------------------------------------------------

interface DayEntry {
  dateStr: string; // "YYYY-MM-DD"
  return: number; // raw decimal
}

function flattenDays(data: PerformancePayload): DayEntry[] {
  const out: DayEntry[] = [];
  for (const [year, yp] of Object.entries(data).sort()) {
    for (const m of MONTH_ORDER) {
      const mp = yp.months[m];
      if (!mp) continue;
      const mi = MONTH_INDEX[m];
      for (const [ds, dp] of Object.entries(mp.days)) {
        out.push({
          dateStr: `${year}-${String(mi + 1).padStart(2, '0')}-${ds}`,
          return: dp.return,
        });
      }
    }
  }
  return out.sort((a, b) => a.dateStr.localeCompare(b.dateStr));
}

// Downloads the full daily-returns series (ITD, independent of whatever
// scope/granularity the charts below are currently set to — "download my
// daily returns" means the complete history, not just what's on screen).
function downloadDailyReturnsCsv(data: PerformancePayload, accountName?: string): void {
  const days = flattenDays(data);
  const rows = ['Date,Return', ...days.map((d) => `${d.dateStr},${d.return}`)];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const safeName = (accountName ?? 'account').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName}_daily_returns.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function applyScope(entries: DayEntry[], scope: Scope): DayEntry[] {
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth();
  if (scope === 'YTD') return entries.filter((e) => Number(e.dateStr.slice(0, 4)) === cy);
  if (scope === 'MTD')
    return entries.filter(
      (e) => Number(e.dateStr.slice(0, 4)) === cy && Number(e.dateStr.slice(5, 7)) - 1 === cm,
    );
  return entries;
}

function dateToTs(dateStr: string): UTCTimestamp {
  const [y, m, d] = dateStr.split('-').map(Number);
  return (Date.UTC(y, m - 1, d) / 1000) as UTCTimestamp;
}

// Crypto week: each bucket starts on Friday 00:00 UTC.
function cryptoWeekKey(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  const daysBack = (d.getUTCDay() + 2) % 7; // 0 if Friday, 6 if Thursday
  d.setUTCDate(d.getUTCDate() - daysBack);
  return d.toISOString().slice(0, 10);
}

function computeBars(
  data: PerformancePayload,
  days: DayEntry[],
  scope: Scope,
  gran: Granularity,
): { time: UTCTimestamp; value: number }[] {
  const filtered = applyScope(days, scope);

  if (gran === 'D') {
    return filtered.map((e) => ({ time: dateToTs(e.dateStr), value: e.return * 100 }));
  }

  if (gran === 'W') {
    const buckets = new Map<string, number[]>();
    for (const e of filtered) {
      const key = cryptoWeekKey(e.dateStr);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(e.return);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, rets]) => ({
        time: dateToTs(key),
        value: (rets.reduce((acc, r) => acc * (1 + r), 1) - 1) * 100,
      }));
  }

  // Monthly
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth();
  const bars: { time: UTCTimestamp; value: number }[] = [];
  for (const [year, yp] of Object.entries(data).sort()) {
    const y = Number(year);
    for (const m of MONTH_ORDER) {
      const mp = yp.months[m];
      if (!mp) continue;
      const mi = MONTH_INDEX[m];
      if (scope === 'YTD' && y !== cy) continue;
      if (scope === 'MTD' && (y !== cy || mi !== cm)) continue;
      bars.push({
        time: (Date.UTC(y, mi, 1) / 1000) as UTCTimestamp,
        value: mp.monthly_return * 100,
      });
    }
  }
  return bars;
}

function computeRawReturns(
  data: PerformancePayload,
  days: DayEntry[],
  scope: Scope,
  gran: Granularity,
): number[] {
  const filtered = applyScope(days, scope);
  if (gran === 'D') return filtered.map((e) => e.return);
  if (gran === 'W') {
    const buckets = new Map<string, number[]>();
    for (const e of filtered) {
      const key = cryptoWeekKey(e.dateStr);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(e.return);
    }
    return [...buckets.values()].map((rets) => rets.reduce((acc, r) => acc * (1 + r), 1) - 1);
  }
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth();
  const result: number[] = [];
  for (const [year, yp] of Object.entries(data).sort()) {
    const y = Number(year);
    for (const m of MONTH_ORDER) {
      const mp = yp.months[m];
      if (!mp) continue;
      const mi = MONTH_INDEX[m];
      if (scope === 'YTD' && y !== cy) continue;
      if (scope === 'MTD' && (y !== cy || mi !== cm)) continue;
      result.push(mp.monthly_return);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// ReturnBarChart — lightweight-charts histogram with per-bar colour
// ---------------------------------------------------------------------------

interface BarPoint {
  time: UTCTimestamp;
  value: number;
  color: string;
}

// Formats a UTCTimestamp (seconds) back to a plain UTC date string for the
// hover tooltip, e.g. "09 Jul 2026". Works for daily/weekly/monthly bars
// alike — weekly/monthly bars are just anchored on their bucket's start date.
const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const fmtBarDate = (time: UTCTimestamp): string => {
  const d = new Date((time as number) * 1000);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

interface TooltipState {
  x: number;
  y: number;
  date: string;
  value: number;
}

function ReturnBarChart({
  data,
  isDark,
}: {
  data: BarPoint[];
  isDark: boolean;
}): React.ReactElement {
  const container = React.useRef<HTMLDivElement>(null);
  const chartRef = React.useRef<IChartApi | undefined>(undefined);
  const seriesRef = React.useRef<ISeriesApi<any> | undefined>(undefined);
  const hasBeenSizedRef = React.useRef(false);
  const [tooltip, setTooltip] = React.useState<TooltipState | null>(null);

  // Create chart once on mount
  React.useEffect(() => {
    if (!container.current) return;
    const gc = isDark ? '#1f3645' : 'rgba(9,45,58,0.08)';
    const tc = isDark ? 'rgba(194,229,255,0.5)' : 'rgba(9,45,58,0.5)';

    const chart = createChart(container.current, {
      width: container.current.clientWidth,
      height: container.current.clientHeight || 200,
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: tc },
      grid: { vertLines: { color: gc }, horzLines: { color: gc } },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: { borderColor: gc, timeVisible: true },
      rightPriceScale: { autoScale: true },
      localization: { priceFormatter: (p: number) => `${p >= 0 ? '+' : ''}${p.toFixed(2)}%` },
    });

    const s = chart.addHistogramSeries({
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    });

    chartRef.current = chart;
    seriesRef.current = s;

    // Hover tooltip — lightweight-charts doesn't ship one, so we track the
    // crosshair ourselves and render a small floating box positioned at the
    // hovered bar's pixel coordinates. Cleared when the pointer leaves the
    // chart (param.point undefined) rather than just the series area, so it
    // doesn't get stuck showing a stale value.
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || !param.time) {
        setTooltip(null);
        return;
      }
      const point = param.seriesData.get(s) as { value?: number } | undefined;
      if (!point || typeof point.value !== 'number') {
        setTooltip(null);
        return;
      }
      setTooltip({
        x: param.point.x,
        y: param.point.y,
        date: fmtBarDate(param.time as UTCTimestamp),
        value: point.value,
      });
    });

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        chart.applyOptions({ width: width - 2, height });

        // Charts mounted while their tab is hidden (display: none) get fitted
        // to a zero-width container. Refit once, the first time we observe a
        // real size, so the zoom level isn't stuck at that degenerate fit.
        if (!hasBeenSizedRef.current) {
          hasBeenSizedRef.current = true;
          chart.timeScale().fitContent();
        }
      }
    });
    ro.observe(container.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = undefined;
      seriesRef.current = undefined;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Theme updates
  React.useEffect(() => {
    if (!chartRef.current) return;
    const gc = isDark ? '#1f3645' : 'rgba(9,45,58,0.08)';
    const tc = isDark ? 'rgba(194,229,255,0.5)' : 'rgba(9,45,58,0.5)';
    chartRef.current.applyOptions({
      layout: { textColor: tc },
      grid: { vertLines: { color: gc }, horzLines: { color: gc } },
      timeScale: { borderColor: gc },
    });
  }, [isDark]);

  // Data updates
  React.useEffect(() => {
    const s = seriesRef.current;
    const chart = chartRef.current;
    if (!s || !chart || data.length === 0) return;
    s.setData(data);
    chart.timeScale().fitContent();
  }, [data]);

  const tooltipBg = isDark ? '#0d1f2a' : '#ffffff';
  const tooltipBorder = isDark ? '#1f3645' : 'rgba(9, 45, 58, 0.15)';
  const tooltipText = isDark ? '#e8f4fd' : '#092D3A';

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Watermark isDark={isDark} />
      <Box ref={container} sx={{ width: '100%', height: '100%', position: 'relative' }} />
      {tooltip && (
        <Box
          sx={{
            position: 'absolute',
            left: Math.min(tooltip.x + 12, (container.current?.clientWidth ?? 0) - 110),
            top: Math.max(tooltip.y - 44, 4),
            pointerEvents: 'none',
            zIndex: 2,
            backgroundColor: tooltipBg,
            border: `1px solid ${tooltipBorder}`,
            borderRadius: '4px',
            px: 1,
            py: 0.5,
            boxShadow: '0px 2px 6px rgba(0,0,0,0.25)',
          }}
        >
          <Typography sx={{ fontSize: 9, color: tooltipText, opacity: 0.6, lineHeight: 1.3 }}>
            {tooltip.date}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.3,
              color: returnText(tooltip.value / 100, isDark),
            }}
          >
            {tooltip.value >= 0 ? '+' : ''}
            {tooltip.value.toFixed(2)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ReturnDistribution — div-based frequency histogram (HTML labels, no SVG text)
// ---------------------------------------------------------------------------

function ReturnDistribution({
  data,
  isDark,
}: {
  data: number[];
  isDark: boolean;
}): React.ReactElement {
  const muted = isDark ? 'rgba(194,229,255,0.3)' : 'rgba(9,45,58,0.3)';

  if (data.length < 3) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 11, color: muted }}>insufficient data</Typography>
      </Box>
    );
  }

  const BIN_COUNT = 20;
  const minR = Math.min(...data);
  const maxR = Math.max(...data);
  const range = maxR - minR || 1e-6;
  const binWidth = range / BIN_COUNT;

  const bins: number[] = new Array(BIN_COUNT).fill(0);
  for (const r of data) {
    const i = Math.min(Math.floor((r - minR) / binWidth), BIN_COUNT - 1);
    bins[i]++;
  }
  const maxCount = Math.max(...bins, 1);
  const zeroPos = minR < 0 && maxR > 0 ? (-minR / range) * 100 : null;

  const labelSx = {
    position: 'absolute' as const,
    fontSize: 10,
    color: muted,
    lineHeight: 1,
    userSelect: 'none' as const,
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Bars */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          gap: '1px',
          minHeight: 0,
        }}
      >
        <Watermark isDark={isDark} />
        {zeroPos !== null && (
          <Box
            sx={{
              position: 'absolute',
              left: `${zeroPos}%`,
              top: 0,
              bottom: 0,
              width: '1px',
              borderLeft: `1px dashed ${muted}`,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
        {bins.map((count, i) => {
          const mid = minR + (i + 0.5) * binWidth;
          const bg =
            mid >= 0
              ? isDark
                ? 'rgba(54,200,138,0.55)'
                : 'rgba(54,200,138,0.65)'
              : isDark
                ? 'rgba(216,44,13,0.55)'
                : 'rgba(216,44,13,0.65)';
          return (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: `${(count / maxCount) * 100}%`,
                backgroundColor: bg,
                borderRadius: '2px 2px 0 0',
                minWidth: 0,
              }}
            />
          );
        })}
      </Box>

      {/* X axis line */}
      <Box sx={{ height: '1px', backgroundColor: muted }} />

      {/* X axis labels */}
      <Box sx={{ position: 'relative', height: 18, mt: 0.5 }}>
        <Typography sx={{ ...labelSx, left: 0 }}>{fmtPct(minR)}</Typography>
        {zeroPos !== null && (
          <Typography sx={{ ...labelSx, left: `${zeroPos}%`, transform: 'translateX(-50%)' }}>
            0%
          </Typography>
        )}
        <Typography sx={{ ...labelSx, right: 0 }}>{fmtPct(maxR)}</Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PerformanceCharts — bar chart + distribution with scope/granularity toggles
// ---------------------------------------------------------------------------

function PerformanceCharts({
  data,
  isDark,
}: {
  data: PerformancePayload;
  isDark: boolean;
}): React.ReactElement {
  const [scope, setScope] = React.useState<Scope>('YTD');
  const [gran, setGran] = React.useState<Granularity>('D');

  const allDays = React.useMemo(() => flattenDays(data), [data]);

  const posColor = isDark ? 'rgba(54,200,138,0.7)' : 'rgba(54,200,138,0.8)';
  const negColor = isDark ? 'rgba(216,44,13,0.7)' : 'rgba(216,44,13,0.8)';

  const barData = React.useMemo(
    (): BarPoint[] =>
      computeBars(data, allDays, scope, gran).map((p) => ({
        ...p,
        color: p.value >= 0 ? posColor : negColor,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allDays, data, scope, gran, isDark],
  );

  const distData = React.useMemo(
    () => computeRawReturns(data, allDays, scope, gran),
    [allDays, data, scope, gran],
  );

  const muted = isDark ? 'rgba(194,229,255,0.4)' : 'rgba(9,45,58,0.4)';
  const divider = isDark ? 'rgba(194,229,255,0.15)' : 'rgba(9,45,58,0.15)';
  const labelSx = {
    fontSize: 10,
    color: muted,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
        <ReportFilterButtons
          titleFilter=""
          dataArr={SCOPE_OPTIONS}
          selected={scope}
          setSelected={(v) => setScope(v as Scope)}
          disableBorder
        />
        <Box sx={{ width: '1px', height: 18, backgroundColor: divider, mx: 0.5 }} />
        <ReportFilterButtons
          titleFilter=""
          dataArr={GRANULARITY_OPTIONS}
          selected={gran}
          setSelected={(v) => setGran(v as Granularity)}
          disableBorder
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 2, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={labelSx}>Returns</Typography>
          <Box sx={{ height: 320 }}>
            <ReturnBarChart data={barData} isDark={isDark} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={labelSx}>Distribution</Typography>
          <Box sx={{ height: 320 }}>
            <ReturnDistribution data={distData} isDark={isDark} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// MonthlyReturnsTable
// ---------------------------------------------------------------------------

interface Selected {
  year: string;
  month: Month;
}

interface MonthlyReturnsTableProps {
  data: PerformancePayload;
  isDark: boolean;
  onSelect: (selected: Selected) => void;
}

function MonthlyReturnsTable({
  data,
  isDark,
  onSelect,
}: MonthlyReturnsTableProps): React.ReactElement {
  const baseText = isDark ? '#e8f4fd' : '#092D3A';
  const muted = isDark ? 'rgba(232, 244, 253, 0.35)' : 'rgba(9, 45, 58, 0.35)';
  const headerBg = isDark ? 'rgba(40, 114, 226, 0.10)' : 'rgba(40, 114, 226, 0.06)';
  const gridLine = isDark ? '1px solid #1f3645' : '1px solid rgba(9, 45, 58, 0.10)';

  const years = Object.keys(data).sort();

  const headCellSx = {
    px: 1,
    py: 1,
    textAlign: 'center' as const,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: baseText,
    backgroundColor: headerBg,
    borderBottom: gridLine,
  };

  return (
    <Box
      component="table"
      sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: '2px', tableLayout: 'fixed' }}
    >
      <Box component="thead">
        <Box component="tr">
          <Box component="th" sx={{ ...headCellSx, textAlign: 'left', width: 56 }}>
            Year
          </Box>
          {MONTH_ORDER.map((m) => (
            <Box component="th" key={m} sx={headCellSx}>
              {m.charAt(0) + m.slice(1).toLowerCase()}
            </Box>
          ))}
          <Box component="th" sx={headCellSx}>
            Annual
          </Box>
        </Box>
      </Box>
      <Box component="tbody">
        {years.map((year) => {
          const yp = data[year];
          return (
            <Box component="tr" key={year}>
              <Box
                component="td"
                sx={{
                  px: 1,
                  py: 1,
                  fontSize: 12,
                  fontWeight: 700,
                  color: baseText,
                  borderBottom: gridLine,
                }}
              >
                {year}
              </Box>
              {MONTH_ORDER.map((m) => {
                const mp = yp.months[m];
                if (!mp) {
                  return (
                    <Box
                      component="td"
                      key={m}
                      sx={{ textAlign: 'center', fontSize: 12, color: muted, py: 1 }}
                    >
                      —
                    </Box>
                  );
                }
                return (
                  <Box
                    component="td"
                    key={m}
                    onClick={() => onSelect({ year, month: m })}
                    sx={{
                      textAlign: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                      color: returnText(mp.monthly_return, isDark),
                      backgroundColor: returnBg(mp.monthly_return),
                      borderRadius: '4px',
                      py: 1,
                      cursor: 'pointer',
                      transition: 'filter 120ms ease',
                      '&:hover': { filter: 'brightness(1.12)' },
                    }}
                  >
                    {fmtPct(mp.monthly_return)}
                  </Box>
                );
              })}
              <Box
                component="td"
                sx={{
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  color: yp.annual_return == null ? muted : returnText(yp.annual_return, isDark),
                  backgroundColor:
                    yp.annual_return == null ? 'transparent' : returnBg(yp.annual_return, 0.04),
                  borderRadius: '4px',
                  py: 1,
                }}
              >
                {yp.annual_return == null ? '—' : fmtPct(yp.annual_return)}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// DayCalendarModal
// ---------------------------------------------------------------------------

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface DayCalendarModalProps {
  selected: Selected | null;
  data: PerformancePayload;
  isDark: boolean;
  onClose: () => void;
}

function DayCalendarModal({
  selected,
  data,
  isDark,
  onClose,
}: DayCalendarModalProps): React.ReactElement {
  const open = selected != null;
  const baseText = isDark ? '#e8f4fd' : '#092D3A';
  const muted = isDark ? 'rgba(232, 244, 253, 0.45)' : 'rgba(9, 45, 58, 0.45)';
  const dialogBg = isDark ? '#0d1f2a' : '#ffffff';
  const emptyBg = isDark ? 'rgba(40, 114, 226, 0.06)' : 'rgba(9, 45, 58, 0.03)';

  let title = '';
  let cells: React.ReactNode[] = [];

  if (selected) {
    const { year, month } = selected;
    const yearNum = Number(year);
    const monthIndex = MONTH_INDEX[month];
    const mp = data[year].months[month];
    title = `${MONTH_FULL[month]} ${year}`;
    const daysInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();
    const firstDow = new Date(yearNum, monthIndex, 1).getDay();
    const leadBlanks = (firstDow + 6) % 7;
    const nodes: React.ReactNode[] = [];
    for (let i = 0; i < leadBlanks; i += 1) nodes.push(<Box key={`lead-${i}`} />);
    for (let d = 1; d <= daysInMonth; d += 1) {
      const key = String(d).padStart(2, '0');
      const dp = mp?.days[key];
      const hasData = dp != null;
      nodes.push(
        <Box
          key={`day-${d}`}
          sx={{
            minHeight: 52,
            borderRadius: '4px',
            p: 0.5,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: hasData ? returnBg(dp!.return) : emptyBg,
            color: hasData ? returnText(dp!.return, isDark) : muted,
          }}
        >
          <Typography sx={{ fontSize: 10, lineHeight: 1, opacity: 0.75 }}>{d}</Typography>
          {hasData && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {fmtPctDay(dp!.return)}
            </Box>
          )}
        </Box>,
      );
    }
    cells = nodes;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: dialogBg, backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ color: baseText, fontSize: 16, fontWeight: 600, pr: 6 }}>
        {title}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: muted }}
          size="small"
          aria-label="close"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', mb: 1 }}>
          {WEEKDAY_HEADERS.map((w) => (
            <Typography
              key={w}
              sx={{
                textAlign: 'center',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.04em',
                color: muted,
              }}
            >
              {w}
            </Typography>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
          {cells}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// PerformanceCard
// ---------------------------------------------------------------------------

interface PerformanceCardProps {
  data: PerformancePayload | null | undefined;
  isLoading?: boolean;
  accountName?: string;
  calculatedAt?: string | null;
}

export function PerformanceCard({
  data,
  isLoading,
  accountName,
  calculatedAt,
}: PerformanceCardProps): React.ReactElement {
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';
  const [selected, setSelected] = React.useState<Selected | null>(null);

  const titleColour = isDark ? '#c2e5ff' : '#092D3A';
  const subColour = isDark ? 'rgba(194, 229, 255, 0.55)' : 'rgba(9, 45, 58, 0.55)';
  const muted = isDark ? 'rgba(194,229,255,0.4)' : 'rgba(9,45,58,0.4)';

  const hasData = !!data && Object.keys(data).length > 0;

  return (
    <Box
      sx={{
        border: isDark ? '1px solid #026aa2' : '1px solid rgba(9, 45, 58, 0.12)',
        backgroundColor: isDark ? 'rgba(40, 114, 226, 0.10)' : '#ffffff',
        borderRadius: '16px',
        boxShadow: '0px 1px 2px 0px #00000059, 0px 2px 6px 2px #00000026',
        p: 2.5,
        boxSizing: 'border-box',
        minHeight: 920,
      }}
    >
      <Box
        sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <Box>
          <Typography sx={{ color: titleColour, fontSize: 15, fontWeight: 600 }}>
            Performance
          </Typography>
          <Typography sx={{ color: subColour, fontSize: 12 }}>
            Monthly returns — click a cell for the daily breakdown
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {calculatedAt && (
            <Typography sx={{ color: subColour, fontSize: 11 }}>
              Metrics as of {fmtCalculatedAt(calculatedAt)}
            </Typography>
          )}
          {hasData && data && (
            <Tooltip title="Download daily returns (CSV)">
              <IconButton
                size="small"
                onClick={() => downloadDailyReturnsCsv(data, accountName)}
                aria-label="download-daily-returns-csv"
                sx={{ color: subColour }}
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {hasData && data ? (
        <>
          <MonthlyReturnsTable data={data} isDark={isDark} onSelect={setSelected} />
          <PerformanceCharts data={data} isDark={isDark} />

          <DayCalendarModal
            selected={selected}
            data={data}
            isDark={isDark}
            onClose={() => setSelected(null)}
          />
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography sx={{ fontSize: 12, color: muted }}>
            {isLoading ? 'Loading performance history…' : 'No performance history yet'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
